// ⛔ THIS FILE IMPORTS NEITHER APPLICATION. It decides which one to fetch, and nothing else — so
// the entry chunk stays tiny and a visitor downloads only the app they actually landed on.
//
// The public booking page is an AD LANDING TARGET: phone, mobile data, one tap from leaving. Before
// this split it was served the entire internal staff application. Measured live 22 Sep 2026: 453 KB
// of JavaScript, and `/slots` was not even requested until 604 ms — the bundle was ~88% of the wait
// and the API 11%. Making `BookingWidget` a `lazy()` chunk inside `App` would NOT have fixed it:
// /book still downloads `index.js`, and `index.js` is what imports `App`.
import { installGlobalErrorReporting } from './lib/errorReporting';
import { isCallInProgress } from './lib/activeCallGuard';
import './index.css';

// ⛔ 22 Sep 2026: catches what ErrorBoundary structurally cannot — a throw inside an event
// handler (not a render) or an unhandled promise rejection. See lib/errorReporting.ts.
// ⛔ Installed HERE, before either entry loads, so it covers the booking page too — that page no
// longer goes through App and would otherwise have lost error reporting entirely.
installGlobalErrorReporting();

// ⛔ 21 Sep 2026: a deploy replaces every hashed asset filename, so a tab that's had the app open
// across a deploy hits "Failed to fetch dynamically imported module: .../SomePage-<oldhash>.js" the
// moment it lazy-loads a route chunk whose file no longer exists — Vite dispatches `vite:preloadError`
// for exactly this. It used to fall straight through to the ErrorBoundary's generic crash screen,
// which is correct but makes an ordinary deploy look like a broken page. A single reload always
// fixes it (a fresh index.html points at the current hashes), so do that automatically instead of
// making someone read a stack trace and click a button. Guarded with sessionStorage against looping
// forever if the fetch is failing for a real reason (network down, asset genuinely missing) — one
// automatic retry, then the normal error screen takes over.
const PRELOAD_RETRY_KEY = 'rth.preloadErrorReloaded';
window.addEventListener('vite:preloadError', () => {
  // ⛔ 13 Oct 2026: never reload under a live (or ringing) phone call — a reload destroys the Twilio Device and
  // drops it, and "answering opens the lead's card" is itself a lazy route load. The failed import still
  // rejects; components/RouteErrorBoundary.tsx catches it in place and reloads once the call is over.
  if (isCallInProgress()) return;
  if (sessionStorage.getItem(PRELOAD_RETRY_KEY)) return; // already retried once — don't loop forever
  sessionStorage.setItem(PRELOAD_RETRY_KEY, '1');
  window.location.reload();
});
// The reload that just happened landed on the CURRENT build, so this page is healthy again — clear
// the guard shortly after mount so the NEXT deploy's preload error (hours or days from now) still
// gets one automatic retry too, instead of silently falling through to the crash screen forever
// because a flag from a past, already-resolved incident was still set.
window.setTimeout(() => sessionStorage.removeItem(PRELOAD_RETRY_KEY), 10_000);

// ⛔ 21 Sep 2026: registers unconditionally (not gated behind push-notification opt-in, unlike
// lib/push.ts's own `register('/sw.js')` call — this one just idempotently reuses the same
// registration) so repeat visits benefit from public/sw.js's asset cache even for someone who never
// turns notifications on. Production only: Vite's dev server serves frequently-changing, unhashed
// module URLs that the worker's own fetch handler is scoped to ignore (`/assets/*` only) — nothing
// would actually get cached in dev — but there's no reason to run a service worker at all against a
// server whose whole model (HMR, on-demand module serving) this fetch handler was never written for.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  });
}

/**
 * The public consultation booking widget, on its own bundle.
 *
 * ⛔ The same pathname test `App` used to run internally, moved one layer out. `/book-interview` is
 * the CANDIDATE interview flow — a different page inside the staff app — so it must not match here.
 * Read from `window.location` rather than a router, because there is no router yet at this point
 * and a full page load is the only way anyone reaches /book.
 */
function isPublicBooking(pathname: string): boolean {
  return pathname.startsWith('/book') && !pathname.startsWith('/book-interview');
}

/**
 * The public contract-signing page — same reasoning as isPublicBooking above. `/sign/:token` is
 * always a cold load from an emailed link (confirmed: no in-app navigation targets it), never an
 * SPA transition, so there's no path that needs the staff app's router state preserved.
 */
function isPublicSigning(pathname: string): boolean {
  return pathname.startsWith('/sign/');
}

/**
 * Five more cold-load-only public pages sharing one entry (publicEntry.tsx) — an overtime
 * approval link, a document view link, a candidate interview-day link, a candidate application
 * form (including the embeddable /apply/general?embed=1 widget), and the candidate
 * interview-booking link. Same confirmation as isPublicSigning: zero in-app navigation to any of
 * these anywhere in apps/web/src, so every real visit is a cold load, never an SPA transition.
 */
function isPublicOther(pathname: string): boolean {
  return (
    pathname === '/ot-approve' ||
    pathname.startsWith('/view/') ||
    pathname.startsWith('/interview-day/') ||
    pathname.startsWith('/apply/') ||
    pathname.startsWith('/book-interview/') ||
    // ⛔ Without this the path falls through to the staff app, which requires a login — so a
    // recipient pressing "Unsubscribe" would land on a LOGIN screen. A broken opt-out.
    pathname.startsWith('/unsubscribe/') ||
    // CV Nurture: the candidate profile a lead opens from an update. Cold load from an email, no login.
    pathname.startsWith('/c/')
  );
}

// ⛔ Dynamic on EVERY branch. A static import of any one of these would pull it into this entry
// chunk and defeat the split entirely — the whole point is that each public page only parses its
// own bundle, and the staff app never parses any public page.
void (isPublicBooking(window.location.pathname)
  ? import('./bookingEntry').then((m) => m.mount())
  : isPublicSigning(window.location.pathname)
    ? import('./signingEntry').then((m) => m.mount())
    : isPublicOther(window.location.pathname)
      ? import('./publicEntry').then((m) => m.mount())
      : import('./appEntry').then((m) => m.mount()));
